/* eslint-disable */
import React, { useEffect, useState } from 'react'
import api from '../../../api'
import Loading from '../../Shared/loading'
import StatusSetter from '../../Shared/StatusSetter'
import { FILLED_STATUSES, shouldShowOvernightBanner } from '../../utils/util'
import * as moment from 'moment-timezone'
import { Work, WorkShift } from '../../Shared/common_defs'
import { dateCommaTimeFormat, getShiftTimezone } from '../../utils/general_util'
import { getLocalTimezone } from 'lib/time'
import { Box, Text, Flex } from '@workwhile/ui'

interface Props {
  shift?: WorkShift
  workId?: number
  workerId?: string
  fetchWorkStats?: (showLoading?: boolean) => void
  fetchWorkers?: (showLoading?: boolean) => void
  onActionSuccess?: (action: string) => void
  removeShiftFromReviewList?: () => void
  hideWorkerInfo?: boolean
}

const statusOptions = [
  { label: 'Pick one', value: null },
  { label: 'scheduled', value: 'scheduled' },
  { label: 'started', value: 'started' },
  { label: 'completed', value: 'completed' },
  { label: 'rejected', value: 'rejected' },
  { label: 'removed', value: 'removed' },
  { label: 'bailed', value: 'bailed' },
  { label: 'admin_review', value: 'admin_review' },
  { label: 'employer_review', value: 'employer_review' },
]

const WorkerDetail = ({
  workerId,
  workId,
  shift,
  fetchWorkStats,
  fetchWorkers,
  onActionSuccess,
  removeShiftFromReviewList,
  hideWorkerInfo,
}: Props) => {
  // use user id passed from prop above to grab user detail info

  const timezone = shift ? getShiftTimezone(shift) : getLocalTimezone()

  const [fetchingWorkDetail, setFetchingWorkDetail] = useState(false)
  const [workDetail, setWorkDetail] = useState<Work | null>(null)
  const [newStatus, setNewStatus] = useState<string | null>(null)
  const [submittingStatus, setSubmittingStatus] = useState(false)
  const [clockIn, setClockIn] = useState('')
  const [clockOut, setClockOut] = useState('')
  const [submittingClockIn, setSubmittingClockIn] = useState(false)
  const [submittingClockOut, setSubmittingClockOut] = useState(false)
  const [submittingLeftEarly, setSubmittingLeftEarly] = useState(false)
  const [userWorkedOvernight, setUserWorkedOvernight] = useState(false)

  useEffect(() => {
    fetchWorkDetail()
  }, [])

  useEffect(() => {
    if (workDetail) {
      if (workDetail.startedAt) {
        const formattedClockIn = moment
          .tz(workDetail.startedAt, timezone)
          .format('YYYY-MM-DDTHH:mm')
        setClockIn(formattedClockIn)
      }
      if (workDetail.completedAt) {
        const formattedClockOut = moment
          .tz(workDetail.completedAt, timezone)
          .format('YYYY-MM-DDTHH:mm')
        setClockOut(formattedClockOut)
      }
    }
  }, [workDetail])

  useEffect(() => {
    if (clockIn && clockOut && shouldShowOvernightBanner(clockIn, clockOut)) {
      setUserWorkedOvernight(true)
    } else if (
      (!clockIn || clockIn === '') &&
      shouldShowOvernightBanner(
        moment.tz(shift?.startsAt, timezone).format('YYYY-MM-DDTHH:mm'),
        clockOut
      )
    ) {
      setUserWorkedOvernight(true)
    } else {
      setUserWorkedOvernight(false)
    }
  }, [clockIn, clockOut])

  const fetchWorkDetail = () => {
    setFetchingWorkDetail(true)
    api
      .post('/admin/graphql_dispatch/work_detail_f78fa47c', { workId })
      .then((response) => {
        const workData = response.data.data.admin.work
        if (workData && workData.length > 0) {
          setWorkDetail(workData[0])
        }
        setFetchingWorkDetail(false)
      })
      .catch(() => {
        setFetchingWorkDetail(false)
      })
  }

  const submitStatusChange = () => {
    if (!workDetail || !shift || !workerId) return
    const currentStatus = workDetail.status
    if (!newStatus) {
      alert('You must select a status.')
      return
    }
    if (newStatus === currentStatus) {
      alert(
        `USER(${workerId}) is already in ${currentStatus?.toUpperCase()} status.`
      )
      return
    }
    setSubmittingStatus(true)
    const r = window.confirm(
      `You are about to change USER(${workerId})'s status to ${newStatus.toUpperCase()}. To execute this action, please press Ok.`
    )
    if (r === true) {
      api
        .post(`/admin/shift/${workerId}/${shift.id}/status/${newStatus}`)
        .then(() => {
          fetchWorkDetail()
          if (fetchWorkStats) fetchWorkStats(false)
          if (fetchWorkers) fetchWorkers(false)
          alert(
            `Changed status to:${newStatus} for user(${workerId})/shift(${shift.id}`
          )
          setNewStatus(null)
          setSubmittingStatus(false)
          if (onActionSuccess) {
            onActionSuccess('set_status')
          }
        })
        .catch(() => {
          setSubmittingStatus(false)
        })
    } else {
      alert('Action Cancelled!')
      setSubmittingStatus(false)
    }
  }

  const submitClockIn = () => {
    if (!workDetail || !shift || !workerId) return
    if (!clockIn || clockIn === '') {
      alert('You must enter a clock in time.')
      return
    }
    setSubmittingClockIn(true)
    const formattedTime = moment.tz(clockIn, timezone)
    const clockedInAtISO = formattedTime.toISOString()

    const r = window.confirm(
      `You are about to change clock in time to:${clockIn} for user(${workerId})/shift(${shift.id}). To execute this action, please press Ok.`
    )
    if (r === true) {
      api
        .post(
          `/admin/shift/${workerId}/${shift.id}/clocked_in/${clockedInAtISO}`
        )
        .then(() => {
          alert(
            `Changed clock in time to:${clockIn} for user(${workerId})/shift(${shift.id}`
          )
          setClockIn('')
          setSubmittingClockIn(false)
          fetchWorkDetail()
          if (onActionSuccess) {
            onActionSuccess('set_clock_in')
          }
        })
        .catch(() => {
          setSubmittingClockIn(false)
        })
    } else {
      alert('Action Cancelled!')
      setSubmittingClockIn(false)
    }
  }

  const submitClockOut = () => {
    if (!workDetail || !shift || !workerId) return
    if (!clockOut || clockOut === '') {
      alert('You must enter a clock out time.')
      return
    }
    setSubmittingClockOut(true)
    const formattedTime = moment.tz(clockOut, timezone)
    const clockedOutAtISO = formattedTime.toISOString()

    const r = window.confirm(
      `You are about to change clock out time to:${clockOut} for user(${workerId})/shift(${shift.id}). To execute this action, please press Ok.`
    )
    if (r === true) {
      api
        .post(
          `/admin/shift/${workerId}/${shift.id}/clocked_out/${clockedOutAtISO}`
        )
        .then(() => {
          alert(
            `Changed clock out time to:${clockOut} for user(${workerId})/shift(${shift.id}`
          )
          setClockOut('')
          setSubmittingClockOut(false)
          fetchWorkDetail()
          if (onActionSuccess) {
            onActionSuccess('set_clock_out')
          }
        })
        .catch(() => {
          setSubmittingClockOut(false)
        })
    } else {
      alert('Action Cancelled!')
      setSubmittingClockOut(false)
    }
  }

  const submitLeftEarly = (left_early: boolean) => {
    if (!workDetail || !shift || !workerId) return
    setSubmittingLeftEarly(true)

    const r = window.confirm(
      `You are about to set USER(${workerId}) left early to ${left_early} for shift(${shift.id}). To execute this action, please press Ok.`
    )

    if (r === true) {
      api
        .post(`/admin/shift/${workerId}/${shift.id}/left_early`, {
          left_early,
        })
        .then(() => {
          alert(
            `Changed left_early to:${left_early} for user(${workerId})/shift(${shift.id}`
          )
          setSubmittingLeftEarly(false)
          fetchWorkDetail()
          if (onActionSuccess) {
            onActionSuccess('set_left_early')
          }
        })
        .catch(() => {
          setSubmittingLeftEarly(false)
        })
    } else {
      alert('Action Cancelled!')
      setSubmittingLeftEarly(false)
    }
  }

  const doneAdminReviewing = () => {
    api.post(`/admin/work/${workId}/admin_review_approve`).then(() => {
      if (removeShiftFromReviewList) {
        removeShiftFromReviewList()
      }
      alert(
        `Successfully done reviewing for assoc(${workId})/user(${workerId})/shift(${shift?.id}`
      )
      if (onActionSuccess) {
        onActionSuccess('done_admin_reviewing')
      }
    })
  }

  const NoShowInfo = ({ workDetail }: any) => {
    if (!workDetail) return null

    if (workDetail.noShowMarkedByShiftLeadId) {
      return (
        <>
          <p>Shift Lead Marked No Show: True</p>
          <p>Shift Lead ID: {workDetail.noShowMarkedByShiftLeadId}</p>
        </>
      )
    }

    return (
      <p style={{ color: workDetail.supervisorMarkedNoShow ? 'red' : 'black' }}>
        Supervisor Marked No Show:{' '}
        {workDetail.supervisorMarkedNoShow ? 'True' : 'False'}
      </p>
    )
  }

  if (fetchingWorkDetail) {
    return <Loading type="button" />
  }

  if (!workDetail) {
    return <p>Could not find work detail for this worker.</p>
  }

  return (
    <div>
      {!hideWorkerInfo && (
        <Box mb={3} p={2} bg="lightBackground" borderRadius={1}>
          <Flex flexDirection="column" gap={1}>
            <Flex flexWrap="wrap" gap={2} alignItems="center">
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Work ID:
                </Text>{' '}
                {workDetail.id}
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Phone:
                </Text>{' '}
                {workDetail.worker?.phoneNumber || 'N/A'}
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Travel Mode:
                </Text>{' '}
                {workDetail.travelMode || 'N/A'}
              </Text>
            </Flex>
            <Flex flexWrap="wrap" gap={2} alignItems="center">
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Confirmed At:
                </Text>{' '}
                {workDetail.confirmedAt || 'N/A'}
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Replaced At:
                </Text>{' '}
                {workDetail.replacedAt || 'N/A'}
              </Text>
              {workDetail.replacedWork?.worker?.name && (
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Replaced By:
                  </Text>{' '}
                  {workDetail.replacedWork.worker.name}
                </Text>
              )}
            </Flex>
            <Flex flexWrap="wrap" gap={2} alignItems="center">
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Total Shifts:
                </Text>{' '}
                {workDetail.worker?.userStats?.shiftCount || 0}
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Penalties:
                </Text>{' '}
                {workDetail.worker?.userStats?.penaltyCount || 0}
              </Text>
            </Flex>
            <Flex flexWrap="wrap" gap={2} alignItems="center">
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Started At:
                </Text>{' '}
                {workDetail.startedAt ? (
                  <Text as="span">
                    {moment
                      .tz(workDetail.startedAt, timezone)
                      .format(dateCommaTimeFormat)}
                  </Text>
                ) : (
                  'N/A'
                )}
              </Text>
              <Text fontSize="sm">
                <Text as="span" fontWeight="bold">
                  Completed At:
                </Text>{' '}
                {workDetail.completedAt ? (
                  <Text as="span">
                    {moment
                      .tz(workDetail.completedAt, timezone)
                      .format(dateCommaTimeFormat)}
                  </Text>
                ) : (
                  'N/A'
                )}
              </Text>
            </Flex>
            {workDetail.trips && workDetail.trips.length > 0 && (
              <Flex flexWrap="wrap" gap={1} alignItems="center">
                <Text fontSize="sm">
                  <Text as="span" fontWeight="bold">
                    Locations:
                  </Text>{' '}
                  {workDetail.trips.map((trip, idx) => (
                    <React.Fragment key={idx}>
                      <a
                        href={trip.embedUrl}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          color: 'inherit',
                          textDecoration: 'underline',
                        }}
                      >
                        location-{idx}
                      </a>
                      {idx < (workDetail.trips?.length || 0) - 1 && ', '}
                    </React.Fragment>
                  ))}
                </Text>
              </Flex>
            )}
          </Flex>
        </Box>
      )}
      <StatusSetter
        label="Set Status"
        name="newStatus"
        value={newStatus as any}
        handleChange={(status) => setNewStatus(status)}
        isLoading={submittingStatus}
        submitStatusChange={submitStatusChange}
        options={statusOptions as any}
        buttonType="confirm"
      />
      {workDetail.status && !FILLED_STATUSES.includes(workDetail.status) ? (
        <div className="alert alert-info" style={{ padding: 2, fontSize: 9 }}>
          Status needs to be SET to a FILLED status (i.e. scheduled, started,
          completed, approved, employer_review, employer_approved, paid) before
          clock in/out times can be set.
        </div>
      ) : null}
      <>
        {workDetail.status && FILLED_STATUSES.includes(workDetail.status) ? (
          <>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>
                Started work at (
                {moment.tz(shift?.startsAt, timezone).format('zz')}):
              </span>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <input
                  type="datetime-local"
                  name="clockIn"
                  value={clockIn}
                  onChange={(e) => setClockIn(e.target.value)}
                ></input>
                {!submittingClockIn && (
                  <button className="confirm-button" onClick={submitClockIn}>
                    Set Start
                  </button>
                )}
                {submittingClockIn ? <Loading type="button" /> : null}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span>
                Finished work at (
                {moment.tz(shift?.endsAt, timezone).format('zz')}):
              </span>
              <div style={{ display: 'flex', flexDirection: 'row' }}>
                <input
                  type="datetime-local"
                  name="clockOut"
                  value={clockOut}
                  onChange={(e) => setClockOut(e.target.value)}
                ></input>
                {!submittingClockOut && (
                  <button className="confirm-button" onClick={submitClockOut}>
                    Set End
                  </button>
                )}
                {submittingClockOut ? <Loading type="button" /> : null}
              </div>
              {userWorkedOvernight ? (
                <div style={{ color: 'red' }}>
                  User({workerId}) worked overnight
                </div>
              ) : null}
            </div>
          </>
        ) : null}
      </>
      <>
        {workDetail.status && FILLED_STATUSES.includes(workDetail.status) ? (
          <>
            <div
              style={{ display: 'flex', flexDirection: 'row', marginTop: 5 }}
            >
              <p style={{ marginRight: 5 }}>Left Early:</p>
              {!submittingLeftEarly && (
                <>
                  <button
                    className="confirm-button"
                    disabled={workDetail.leftEarly}
                    style={{
                      backgroundColor: workDetail.leftEarly ? '#27c8b6' : '',
                      marginRight: 5,
                    }}
                    onClick={() => submitLeftEarly(true)}
                  >
                    {workDetail.leftEarly ? '✓ ' : null}True
                  </button>
                  <button
                    className="confirm-button"
                    disabled={!workDetail.leftEarly}
                    style={{
                      backgroundColor: workDetail.leftEarly ? '' : '#27c8b6',
                    }}
                    onClick={() => submitLeftEarly(false)}
                  >
                    {!workDetail.leftEarly && '✓ '}False
                  </button>
                </>
              )}

              {submittingLeftEarly ? <Loading type="button" /> : null}
            </div>
            {workDetail.leftEarly ? (
              <>
                <p>
                  Worker Reason Early Clockout:{' '}
                  {workDetail.reasonEarlyClockout || 'N/A'}
                </p>
                <p>
                  Supervisor Reason Early Clock Out:{' '}
                  {workDetail.supervisorReasonEarlyClockOut || 'N/A'}
                </p>
              </>
            ) : null}
          </>
        ) : null}
      </>
      <br />
      <br />
      <NoShowInfo workDetail={workDetail} />
      {workDetail.status === 'admin_review' && (
        <button onClick={() => doneAdminReviewing()}>Done reviewing</button>
      )}
      <br />
    </div>
  )
}

export default WorkerDetail
